import { AppError as HttpAppError } from '@/http/errors';
import type { AppError } from '@/features/auth/types';

export function mapHttpError(message: string): string {
  if (!message || !message.startsWith('HTTP.')) {
    return message;
  }

  const httpErrorPattern = /^HTTP\.ERROR\.(\d+)\.(.+)$/;
  const match = message.match(httpErrorPattern);

  if (match) {
    const [, statusStr, errorType] = match;
    const status = parseInt(statusStr, 10);

    switch (errorType) {
      case 'SESSION.IN_PROGRESS.DELETE_FORBIDDEN':
        return 'errors.sessionInProgressDeleteForbidden';
      case 'EVERGREEN.SOURCE_WEBINAR.DELETE_FORBIDDEN':
        return 'errors.evergreenSourceWebinarDeleteForbidden';
      case 'EVERGREEN.SOURCE_SESSION.DELETE_FORBIDDEN':
        return 'errors.evergreenSourceSessionDeleteForbidden';
      case 'EVERGREEN.SOURCE_TIMELINE.PREPARING':
      case 'EVERGREEN.INTERACTION_SOURCE.TIMELINE.PREPARING':
        return 'errors.evergreenSourceTimelinePreparing';
      case 'EVERGREEN.YOUTUBE.START_TIME.UNAVAILABLE':
        return 'errors.evergreenYoutubeStartTimeUnavailable';
      case 'PUBLISH.MISSING.SESSIONS':
        return 'errors.publishMissingSessions';
      case 'PUBLISH.EVERGREEN_NOT_READY':
        return 'errors.publishEvergreenNotReady';
      case 'VALIDATION.ERRORS':
        return 'errors.validation';
      case 'OFFER.CONTENT.EMBED_CODE.INVALID':
        return 'errors.offerEmbedCodeInvalid';
      case 'OFFER.CONTENT.EMBED.THANK_YOU_PAGE.UNSUPPORTED':
      case 'THANK_YOU_PAGE.OFFER.EMBED.UNSUPPORTED':
        return 'errors.offerEmbedThankYouUnsupported';
      case 'OFFER.CONTENT.STRIPE_PRICE_ID.REQUIRED':
        return 'errors.offerStripePriceRequired';
      case 'OFFER.CONTENT.STRIPE_PRICES.DUPLICATE':
        return 'errors.offerStripePriceDuplicate';
      case 'OFFER.CONTENT.STRIPE_PRICES.TOO_MANY':
        return 'errors.offerStripePricesTooMany';
      case 'OFFER.CONTENT.STRIPE_INSTALLMENT_COUNT.INVALID':
        return 'errors.offerStripeInstallmentCountInvalid';
      case 'STRIPE.PRICE.UNAVAILABLE':
        return 'errors.offerStripePriceUnavailable';
      case 'STRIPE.CONNECT.NOT_CONNECTED':
        return 'errors.offerStripeNotConnected';
      case 'OFFER.CONTENT.STRIPE_PRICING_TABLE.UNSUPPORTED':
        return 'errors.offerStripePricingTableUnsupported';
      case 'OFFER.CONTENT.STRIPE.EVERGREEN.UNSUPPORTED':
        return 'errors.offerStripeEvergreenUnsupported';
      case 'OFFER.CONTENT.STRIPE.THANK_YOU_PAGE.UNSUPPORTED':
      case 'THANK_YOU_PAGE.OFFER.STRIPE.UNSUPPORTED':
        return 'errors.offerStripeThankYouUnsupported';
      case 'QUIZ.QUESTION.DUPLICATE':
        return 'errors.quizQuestionDuplicate';
      case 'QUIZ.OPTION.DUPLICATE':
        return 'errors.quizOptionDuplicate';
      case 'ATTENDEE.CSV.INVALID':
        return 'errors.attendeeCsvInvalid';
      case 'ATTENDEE.CSV.FIRST_NAME.COLUMN.REQUIRED':
        return 'errors.attendeeCsvFirstNameColumnRequired';
      case 'ATTENDEE.CSV.EMAIL.COLUMN.REQUIRED':
        return 'errors.attendeeCsvEmailColumnRequired';
      case 'ATTENDEE.CSV.EMAIL.INVALID':
        return 'errors.attendeeCsvEmailInvalid';
      case 'ATTENDEE.CSV.IMPORT.ALREADY.RUNNING':
        return 'errors.attendeeImportAlreadyRunning';
      case 'WORKSPACE.ALREADY.SUBSCRIBED':
        return 'errors.billing.alreadySubscribed';
      case 'BILLING.PROMOTION.FIRST_TRANSACTION.ALREADY_USED':
        return 'errors.billing.promoFirstTransactionUsed';
      case 'STRIPE.PROMO_CODE.NOT_FOUND':
        return 'errors.billing.promoCodeNotFound';
      case 'BILLING.PROMOTION.INVALID_OR_INAPPLICABLE':
        return 'errors.billing.promoInvalidOrInapplicable';
      case 'BILLING.PROMOTION.UPDATE.IN_PROGRESS':
        return 'errors.billing.promoUpdateInProgress';
      case 'BILLING.PROMOTION.PAYMENT_FINALIZED':
        return 'errors.billing.promoPaymentFinalized';
      case 'BILLING.TRIAL.PAYMENT_METHOD.REQUIRED':
        return 'errors.billing.trialPaymentMethodRequired';
      case 'BILLING.PENDING_SUBSCRIPTION.PRICE_MISMATCH':
        return 'errors.billing.pendingSubscriptionPriceMismatch';
      case 'BILLING.PRICE.CURRENCY.MISMATCH':
        return 'errors.billing.priceCurrencyMismatch';
      case 'STRIPE.REQUEST_IN_PROGRESS':
        return 'errors.billing.subscriptionRequestInProgress';
      case 'WORKSPACE.ADMIN.REQUIRED':
        return 'errors.forbidden';
      case 'WRP.IMAGE.FILE.REQUIRED':
        return 'errors.fileRequired';
      case 'UPLOAD.MAX_SIZE_EXCEEDED':
        return 'errors.uploadMaxSizeExceeded';
      case 'INTERNAL.SERVER.ERROR':
        return 'errors.serverError';
      case 'YOUTUBE.PLAN_NOT_ELIGIBLE':
        return 'errors.youtube.planNotEligible';
      case 'WEBINAR.YOUTUBE_URL.MISSING':
        return 'errors.youtube.urlMissing';
      case 'WEBINAR.YOUTUBE_URL.TOO_LONG':
        return 'errors.youtube.urlTooLong';
      case 'WEBINAR.YOUTUBE_URL.INVALID_FORMAT':
        return 'errors.youtube.urlInvalid';
      case 'WEBINAR.YOUTUBE_URL.NOT_FOUND':
        return 'errors.youtube.urlNotFound';
      case 'WEBINAR.YOUTUBE_URL.NOT_EMBEDDABLE':
        return 'errors.youtube.urlNotEmbeddable';
      case 'WEBINAR.YOUTUBE_URL.UNAVAILABLE':
        return 'errors.youtube.urlUnavailable';
      default:
        const parts = errorType.split('.');
        const lastPart = parts[parts.length - 1];

        if (parts.length >= 2) {
          const fieldNamePart = parts[parts.length - 2];
          const normalizedFieldName = fieldNamePart.toLowerCase().replace(/_/g, '');

          if (
            [
              'MISSING',
              'REQUIRED',
              'INVALID',
              'TOO_LONG',
              'TOO_SHORT',
              'MAX_LENGTH',
              'MIN_LENGTH',
            ].includes(lastPart)
          ) {
            const errorKey = lastPart.toLowerCase();
            return `errors.${normalizedFieldName}.${errorKey}`;
          }
        }

        if (lastPart) {
          switch (lastPart) {
            case 'MISSING':
            case 'REQUIRED':
              return 'errors.fieldRequired';
            case 'INVALID':
              return 'errors.fieldInvalid';
            case 'TOO_LONG':
            case 'MAX_LENGTH':
              return 'errors.fieldTooLong';
            case 'TOO_SHORT':
            case 'MIN_LENGTH':
              return 'errors.fieldTooShort';
          }
        }

        return mapApiErrorByStatus(status);
    }
  }

  if (message.startsWith('HTTP.')) {
    return 'errors.generic';
  }

  return message;
}

function mapApiErrorByStatus(status: number): string {
  switch (status) {
    case 400:
      return 'errors.badRequest';
    case 401:
      return 'errors.unauthorized';
    case 403:
      return 'errors.forbidden';
    case 404:
      return 'errors.notFound';
    case 409:
      return 'errors.conflict';
    case 422:
      return 'errors.validation';
    case 429:
      return 'errors.tooManyRequests';
    default:
      if (status >= 500) {
        return 'errors.serverError';
      }
      return 'errors.generic';
  }
}

export function mapErrorMessage(message?: string, code?: string, status?: number): string {
  if (message && message.startsWith('errors.')) {
    return message;
  }

  if (message && message.startsWith('HTTP.')) {
    return mapHttpError(message);
  }

  return mapApiError(code || 'UNKNOWN', status);
}

export function mapApiError(code: string, status?: number): string {
  if (code && code.startsWith('errors.')) {
    return code;
  }

  if (code && code.startsWith('HTTP.')) {
    return mapHttpError(code);
  }

  switch (code) {
    case 'UNAUTHORIZED':
      return 'errors.unauthorized';
    case 'FORBIDDEN':
      return 'errors.forbidden';
    case 'NOT_FOUND':
      return 'errors.notFound';
    case 'VALIDATION':
      return 'errors.validation';
    case 'CONFLICT':
      return 'errors.conflict';
    case 'RATE_LIMIT':
      return 'errors.tooManyRequests';
    case 'SERVER':
      return 'errors.serverError';
    case 'NETWORK':
      return 'errors.network';
    default:
      if (status) {
        return mapApiErrorByStatus(status);
      }

      return 'errors.generic';
  }
}

/**
 * Convertit une erreur catchée en payload rejectValue sérialisable (plain object). Ne jamais passer
 * directement une instance AppError/Error à rejectWithValue : Redux la stocke telle quelle dans
 * l'action, ce qui déclenche l'avertissement "non-serializable value" du middleware (AppError
 * embarque un stack trace et potentiellement l'erreur Axios brute via `raw`).
 */
export function toRejectValue(err: unknown): AppError {
  if (err instanceof HttpAppError) {
    return {
      code: err.code,
      message: mapErrorMessage(err.message, err.code, err.status),
      details: err.details,
    };
  }

  const e = err as { code?: string; message?: string; status?: number };
  return {
    code: e.code ?? 'UNKNOWN',
    message: mapErrorMessage(e.message, e.code, e.status),
  };
}
